import { VsmStatus } from "@/entities/vsm";
import { Badge, Button, Card, Inputfield, Selectfield } from "@/shared/components";
import { convertToKebabCase, omitBy } from "@/shared/utils";
import { MaatregelProvider } from "../maatregel-provider";
import { useMaatregelenEditor, useVsmEditorContext } from "../vsm-editor-context";
import { useIsVsmStatusGewijzigd } from "../vsm-vergelijking-context";
import { Versperringsmaatregel } from "./versperringsmaatregel";
import { VsmMaatregelList, VsmSectionTitle } from "./vsm-details";

const VsmStatusOptions = Object.values(omitBy(VsmStatus, (value) => value === "Gestopt")).map((status) => ({
    value: status,
    label: status,
}));

export function EditableVsm() {
    const { status, setStatus, toelichting, setToelichting } = useVsmEditorContext();
    const isStatusGewijzigd = useIsVsmStatusGewijzigd();

    return (
        <Card className="gap-4 p-4">
            <div className="flex items-end gap-2">
                <Selectfield
                    label="Status"
                    name="status"
                    data-testid="vsm-status"
                    options={VsmStatusOptions}
                    value={status}
                    onValueChange={(status: string) => setStatus(status as VsmStatus)}
                    className="text-content-default w-1/2"
                />
                {isStatusGewijzigd && (
                    <Badge variant="yellow-subtle" className="mb-2" data-testid="vsm-status-gewijzigd">
                        Gewijzigd
                    </Badge>
                )}
            </div>
            <EditableVsmSection type="RijdtNiet" />
            <EditableVsmSection type="Rijdt" />
            <EditableVsmSection type="Aanvullend" />
            <div className="text-content-default">
                <Inputfield
                    label="Toelichting VSM"
                    optional={true}
                    value={toelichting}
                    onValueChange={setToelichting}
                    helptext="Toelichtingen worden na toevoegen aan medewerkers in Wissel getoond"
                    data-testid="toelichting"
                />
            </div>
        </Card>
    );
}

type EditableVsmSectionProps = {
    type: "Rijdt" | "RijdtNiet" | "Aanvullend";
};

function EditableVsmSection({ type }: Readonly<EditableVsmSectionProps>) {
    const { startDraft, draftMaatregel, isEditEnabled, maatregelen, isSectionEmpty, ...editorProps } = useMaatregelenEditor(type);

    return (
        <div className="flex w-full flex-col gap-2" data-testid={`vsm-section-${convertToKebabCase(type)}`}>
            <div className="flex h-8 flex-row items-center justify-between">
                <VsmSectionTitle headerLevel="h5" type={type} />
                <Button
                    type="button"
                    onClick={startDraft}
                    disabled={!isEditEnabled}
                    className="text-inherit group-data-[variant=oud]:hidden"
                    iconOnly
                    variant="tertiary"
                    icon="plus"
                    data-testid="add-maatregel-button"
                />
            </div>
            <MaatregelProvider variant="nieuw" intent="update" isEditEnabled={isEditEnabled} {...editorProps}>
                <VsmMaatregelList maatregelen={maatregelen} isSectionEmpty={isSectionEmpty} />
            </MaatregelProvider>
            {draftMaatregel && (
                <MaatregelProvider variant="nieuw" intent="create" isEditEnabled={isEditEnabled} {...editorProps}>
                    <Versperringsmaatregel maatregel={draftMaatregel} />
                </MaatregelProvider>
            )}
        </div>
    );
}
